import * as Sentry from "@sentry/browser";
import { Address } from "@wagmi/core";
import { cloneDeep } from "lodash-es";
import percentRound from "percent-round";
import { waitForTransaction } from "wagmi/actions";
import { Amount } from "../../types/amount";
import { OrderStatus } from "../../types/orderStatus";
import { WrapType } from "../../types/wrapQuote";
import { getWrappedSymbolForChain } from "../../utils/chainUtils";
import { getOrderType } from "../../utils/orderTypeUtils";
import { getPermitFromStore, getPermitToUse } from "../../utils/permitUtils";
import { INITIAL_RECEIVER_STATE } from "../../utils/swapAndSendUtils";
import { URL_KEYS, updateUrlParams } from "../../utils/urlUtils";
import { attachUserId } from "../../utils/userIdUtils";
import { ActivityStore, INITIAL_STATE_ACTIVITY } from "../activity/ActivityStore";
import { getHistory } from "../activity/getHistory";
import { GlobalStore, SelectedToken } from "./GlobalStore";
import { Token } from "./TokenEnum";
import { getAllowances } from "./getAllowances";
import { getBalances } from "./getBalances";
import { getRoles } from "./getGuildRoles";
import { getQuoteDebounced } from "./getQuote";
import { getTokenMetadata } from "./getTokenMetadata";
import { getZerionNft } from "./getZerionNftImage";
import { sendSignedOrder } from "./signOrder";
import { initialWrapQuote } from "pages/Wrap/elements/WrapEntry";

export const attachReactions = (): void => {
  GlobalStore.createReaction(
    store => {
      return {
        orderType: getOrderType(store),
        userAddress: store.address,
        takerTokens: store.takerTokens,
        tokenMetadata: store.tokens,
        network: store.network,
      };
    },
    watched => {
      const { orderType, userAddress, takerTokens, tokenMetadata, network } = watched;
      if (orderType && userAddress && takerTokens.length > 0 && tokenMetadata && network) {
        getPermitToUse(orderType, userAddress as `0x${string}`, takerTokens, tokenMetadata, network)
          .then(permitType => {
            console.log(`Reaction Determined permit type to be ${permitType}`);
            GlobalStore.update(s => {
              s.permitType = permitType;
            });
          })
          .catch(e => {
            console.error(`Could not determine permit type`, e);
          });
      }
    },
    {
      runNowWithSideEffects: true,
    }
  );
  GlobalStore.createReaction(
    store => store.address,
    (address, draft, originalStore) => {
      if (address) {
        // Reset all user-related data in GlobalStore when address changes
        draft.zerionNft = "";
        ActivityStore.update(s => {
          const { data, isLoading, error } = INITIAL_STATE_ACTIVITY.history;
          s.history = { data, isLoading, error };
        });
        draft.ensName = "";
        draft.ensAvatar = "";
        draft.zerionNft = "";
        draft.userRoles = [];

        void getZerionNft.run(address);
        attachUserId(address);
        // Updated to check store.tokens rather than draft it seemed like this condition would never pass
        if (originalStore.tokens) {
          void getBalances.run({
            address: address as Address,
            tokenMetadata: cloneDeep(originalStore.tokens),
            chain: cloneDeep(originalStore.network),
          });
          void getAllowances.run({
            address: address as Address,
            tokenMetadata: cloneDeep(draft.tokens),
          });
        }
        const activityStoreTokens = ActivityStore.getRawState().tokens;
        if (activityStoreTokens) {
          const TODAY_TIMESTAMP = new Date().getTime() * 1e6;
          void getHistory.run({
            address: draft.address as Address,
            tokenMetadata: activityStoreTokens,
            timestamp: TODAY_TIMESTAMP,
            previousResult: undefined,
          });
        }
        void getRoles.run({ userAddress: address as Address });
      }
    }
  );

  GlobalStore.createReaction(
    store => store.network,
    (network, draft, original, lastWatched) => {
      const address = draft.address.toString();
      if (network && network.name !== lastWatched.name) {
        console.log(`Network changed from ${lastWatched.name} to ${network.name}`);
        // Refresh prices, tokens and balances to get new applicable tokens

        void getTokenMetadata
          .run(network.name)
          .then(res => {
            if (address && res.payload) {
              void getBalances
                .run({
                  address: address as `0x${string}`,
                  tokenMetadata: res.payload,
                  chain: network,
                })
                .catch(err => {
                  const errMsg = `Could not get balances after a network switch`;
                  console.error(errMsg, err);
                  Sentry.captureException(errMsg, { extra: { err: errMsg } });
                });
            }
          })
          .catch(err => {
            const errMsg = `Could not get Tokens after a network switch`;
            console.error(errMsg, err);
            Sentry.captureException(errMsg, { extra: { err: errMsg } });
          });

        // Set network default token to top token.
        // TODO: use resetState function, attempted but didn't work as intended so using this still for now
        const newToken = getWrappedSymbolForChain(network);
        draft.takerTokens = [{ token: newToken, amount: Amount.ZERO }];
        draft.makerTokens = [];
        draft.quote = undefined;
        draft.isRequestingQuote = false;
        draft.order = undefined;
        draft.receiver = INITIAL_RECEIVER_STATE;
        draft.orderError = undefined;
        draft.wrapQuote = initialWrapQuote;
        draft.receiverError = "";
        draft.isSending = false;
        draft.quoteError = undefined;
        draft.tokens = undefined;
        draft.tokenList = [];
      }
    },
    {
      runNowWithSideEffects: true,
    }
  );
  // Update URL Params on change
  GlobalStore.createReaction(
    store => store.makerTokens,
    makerTokens => {
      const seralizedTokens = makerTokens.map(entry => entry.token).join(",");
      updateUrlParams(URL_KEYS.TO, seralizedTokens, window.location.pathname);
    }
  );
  // Update URL Params on change
  GlobalStore.createReaction(
    store => store.takerTokens,
    takerTokens => {
      const seralizedTokens = takerTokens.map(entry => entry.token).join(",");
      updateUrlParams(URL_KEYS.FROM, seralizedTokens, window.location.pathname);
    }
  );
  // Update URL Params on change
  GlobalStore.createReaction(
    store => store.network,
    network => {
      updateUrlParams(URL_KEYS.NETWORK, network.name.toLowerCase(), window.location.pathname);
    }
  );

  // Rounds percentages
  GlobalStore.createReaction(
    store => store.makerTokens,
    (makerTokens, draft, original, lastWatched) => {
      // Only rebalance when tokens are added or removed
      if (lastWatched.length !== makerTokens.length) {
        // Recalculate percentages to avoid irrational numbers
        // i.e. convert [33.3(3), 33.3(3), 33.3(3)] to [34, 33, 33]
        const roundedPercentages = percentRound(makerTokens.map(entry => entry.pct));
        draft.makerTokens = draft.makerTokens.map((entry, index) => {
          entry.pct = roundedPercentages[index];
          return entry;
        });
      }
    }
  );

  // Requesting quote reaction
  GlobalStore.createReaction(
    store => {
      return {
        orderType: getOrderType(store),
        makerTokens: store.makerTokens,
        takerTokens: store.takerTokens,
        network: store.network,
        address: store.address,
        tokens: store.tokens,
        orderStatus: store.orderStatus,
        receiver: store.receiver,
        permits: store.permits,
      };
    },
    (watched, draft) => {
      const takerTokenHasAnAmount = !draft.takerTokens.some(entry => entry.amount.value.isZero());
      const makerTokenSelected = draft.makerTokens.length > 0;
      const takerArray = draft.takerTokens.map(e => e.token);
      const makerArray = draft.makerTokens.map(e => e.token);
      const isUnwrapping =
        takerArray.includes(getWrappedSymbolForChain(draft.network)) &&
        makerArray.includes(draft.network.nativeCurrency.symbol as Token);
      const isWrapping =
        makerArray.includes(getWrappedSymbolForChain(draft.network)) &&
        takerArray.includes(draft.network.nativeCurrency.symbol as Token);
      const quotableOrderState =
        draft.orderStatus === OrderStatus.ENTRY ||
        draft.orderStatus === OrderStatus.QUOTING ||
        draft.orderStatus === OrderStatus.REVIEW;

      if (isWrapping || isUnwrapping) {
        const wrappingOrUnwrapping = isWrapping ? WrapType.Wrapping : WrapType.Unwrapping;
        const amount = draft.takerTokens[0].amount;
        const newWrapQuote = {
          type: wrappingOrUnwrapping,
          amount: amount,
          max: true,
          gas: Amount.ZERO,
          hash: draft.wrapQuote?.hash,
          warning: undefined,
          error: undefined,
        };
        draft.wrapQuote = newWrapQuote;
      }

      if (
        takerTokenHasAnAmount &&
        makerTokenSelected &&
        watched.orderType &&
        quotableOrderState &&
        !isWrapping &&
        !isUnwrapping
      ) {
        getQuoteDebounced({
          orderType: watched.orderType,
          takerTokens: watched.takerTokens,
          makerTokens: watched.makerTokens,
          network: watched.network,
          address: watched.address as `0x${string}`,
          tokenMetadata: watched.tokens,
          receiver: watched.receiver,
          permitType: draft.permitType,
        });
      }
    },
    {
      runNowWithSideEffects: true,
    }
  );

  // Wait for order tx
  GlobalStore.createReaction(
    store => store.order,
    (watched, draft) => {
      if (watched?.txHash) {
        // Add alert for when a user try's to exit whilst they have put a trade through
        // This is an extra precaution to help with incorrect balances displaying on the UI.
        window.onbeforeunload = function () {
          return true;
        };
        waitForTransaction({
          hash: watched.txHash as `0x${string}`,
        })
          .then(receipt => {
            if (receipt.transactionHash && receipt.status) {
              GlobalStore.update(s => {
                s.orderStatus = OrderStatus.COMPLETED;
              });
            } else {
              const errMsg = `There was an error with the tx receipt.`;
              console.error(errMsg, receipt);
              Sentry.captureException(errMsg, { extra: { err: JSON.stringify(receipt) } });
              GlobalStore.update(s => {
                s.orderStatus = OrderStatus.ERROR;
              });
            }
          })
          .catch(e => {
            const errMsg = `Order Tx did not complete`;
            console.log(errMsg);
            console.error(e);
            Sentry.captureException(errMsg, { extra: { err: e } });
            GlobalStore.update(s => {
              s.orderStatus = OrderStatus.ERROR;
            });
          });
      }
    },
    {
      runNowWithSideEffects: true,
    }
  );

  // Rounds percentages
  GlobalStore.createReaction(
    store => store.quote,
    (quote, draft, original, lastWatched) => {
      console.log(`Quote id is`, quote?.quoteId);
      if (lastWatched?.quoteId && quote === undefined) {
        draft.orderStatus = OrderStatus.ENTRY;
        return;
      }
      if (lastWatched?.quoteId !== quote?.quoteId) {
        if (draft.orderStatus === OrderStatus.ENTRY) {
          draft.orderStatus = OrderStatus.QUOTING;
        }
      }
    }
  );

  GlobalStore.createReaction(
    store => store.signedMessage,
    (signedMessage, draft) => {
      if (draft.signedMessage) {
        void sendSignedOrder.run({
          message: draft.signedMessage.message,
          chain: draft.signedMessage.chain,
          quote: draft.signedMessage.quote,
          signature: draft.signedMessage.signature,
          permit: getPermitFromStore(draft),
        });
      }
    }
  );

  /**
   * Clear Quote error
   */
  GlobalStore.createReaction(
    store => {
      return {
        network: store.network,
        makerTokens: store.makerTokens,
        takerTokens: store.takerTokens,
        quoteError: store.quoteError,
      };
    },
    (watched, draft, original, lastWatched) => {
      if (!watched.quoteError) {
        return;
      }
      const networkChanged = watched.network.id !== lastWatched.network.id;
      if (networkChanged) {
        watched.quoteError = undefined;
      }
    }
  );

  /**
   * Clear Quote if we don't have a single taker token with a non-zero amount
   */
  GlobalStore.createReaction(
    store => store.takerTokens,
    (takerTokens, draft, original, lastWatched) => {
      if (!takerTokens.some(entry => entry.amount.value.gt(0))) {
        draft.quote = undefined;
        draft.wrapQuote = undefined;
      }
    }
  );

  /**
   * When tokens are changed while quoting - kick back to order entry and wipe out last quote
   */
  GlobalStore.createReaction(
    s => {
      return {
        makerTokens: s.makerTokens,
        takerTokens: s.takerTokens,
        modalSelector: s.modalSelector,
      };
    },
    (watched, draft, original, lastWatched) => {
      const areTokensIdentical = (a: Array<SelectedToken>, b: Array<SelectedToken>) => {
        const aSet = new Set(a.map(e => e.token));
        const bSet = new Set(b.map(e => e.token));
        if (aSet.size !== bSet.size) {
          return false;
        }
        for (const aEntry of aSet.keys()) {
          if (!bSet.has(aEntry)) {
            return false;
          }
        }
        return true;
      };
      const changedMakerTokens = !areTokensIdentical(watched.makerTokens, lastWatched.makerTokens);
      const changedTakerTokens = !areTokensIdentical(watched.takerTokens, lastWatched.takerTokens);
      const takerArray = draft.takerTokens.map(e => e.token);
      const makerArray = draft.makerTokens.map(e => e.token);

      const isUnwrapping =
        takerArray.includes(getWrappedSymbolForChain(draft.network)) &&
        makerArray.includes(draft.network.nativeCurrency.symbol as Token);
      const isWrapping =
        makerArray.includes(getWrappedSymbolForChain(draft.network)) &&
        takerArray.includes(draft.network.nativeCurrency.symbol as Token);

      // If the selected tokens which causes reaction to run isn't wrapping or unwrapping we clear the wrapQuote
      const isOnTradePage = draft.orderStatus !== OrderStatus.ENTRY;
      if (isOnTradePage && !isWrapping && !isUnwrapping) {
        draft.wrapQuote = undefined;
      }

      if (changedMakerTokens || changedTakerTokens) {
        console.log(`Wiping quote`);
        draft.orderStatus = OrderStatus.ENTRY;
        draft.quote = undefined;
        draft.quoteError = undefined;
      }
    }
  );

  /** Remove "Are you sure you want to exit" alert */
  GlobalStore.createReaction(
    store => store.orderStatus,
    orderStatus => {
      if (orderStatus !== OrderStatus.COMPLETED && orderStatus !== OrderStatus.IN_PROGRESS) {
        window.onbeforeunload = null;
      }
    }
  );
};
